import { useEffect, useRef } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { CANAIS_NOTIFICACOES, permissaoNotificacoesAndroid, destinoNotificacao, atualizarStatusPush } from "../utils/notificacoesAndroid";

const isAndroidApp = () => window.Capacitor?.getPlatform?.() === "android";

// app = "admin": celular do app Print Control Admin. O token fica marcado e o
// servidor só manda para ele os avisos de suporte (ver functions/notifications/push.js).
export function usePushNotifications(uid, onAbrir, app = null) {
  const abrirRef = useRef(onAbrir);
  useEffect(() => { abrirRef.current = onAbrir; }, [onAbrir]);

  useEffect(() => {
    if (!uid || !isAndroidApp()) return;
    let cancelado = false, registrando = false, gravando = false;
    let tokenAtual, tokenSalvo, tentativas = 0, retryTimer;
    const listeners = [];
    const status = estado => { if (!cancelado) atualizarStatusPush({ estado, uid }); };
    status("aguardando");
    const abrir = (data = {}) => {
      if (cancelado) return;
      const destino = destinoNotificacao(data, uid);
      if (destino) abrirRef.current?.(destino);
    };
    const tentarDepois = () => {
      if (cancelado || retryTimer || tentativas >= 3) return;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        tentativas++;
        iniciar(false);
      }, 5000 * (tentativas + 1));
    };
    const salvarToken = async () => {
      if (cancelado || gravando || !tokenAtual || tokenAtual === tokenSalvo) return;
      const valor = tokenAtual;
      gravando = true;
      try {
        await setDoc(doc(db, "users", uid, "push_tokens", valor), {
          platform: "android", updatedAt: serverTimestamp(), ...(app ? { app } : {}),
        });
        if (!cancelado) { tokenSalvo = valor; status("registrado"); }
      } catch (e) {
        status("erro");
        console.warn("Não foi possível registrar o celular para notificações:", e?.code || "push-token");
        tentarDepois();
      } finally {
        gravando = false;
        if (!cancelado && tokenAtual !== valor) salvarToken();
      }
    };
    let push;
    const iniciar = async (solicitar = false) => {
      if (cancelado || registrando || !push) return;
      registrando = true;
      try {
        if (!await permissaoNotificacoesAndroid(solicitar)) { status("bloqueado"); return; }
        if (cancelado) return;
        const canais = app === "admin" ? CANAIS_NOTIFICACOES.filter(c => c.id === "pc_suporte") : CANAIS_NOTIFICACOES;
        for (const canal of canais) await push.createChannel(canal);
        if (cancelado) return;
        status(tokenSalvo ? "registrado" : "registrando");
        if (tokenAtual && tokenAtual !== tokenSalvo) await salvarToken();
        // Renovar também ao retomar: cobre permissão concedida depois do login.
        await push.register();
      } catch (e) {
        status("erro");
        console.warn("Falha ao iniciar notificações:", e?.code || "push-registration");
        tentarDepois();
      } finally { registrando = false; }
    };
    const aoRetomar = () => {
      if (document.visibilityState === "hidden") return;
      tentativas = 0;
      iniciar(false);
    };
    const aoTentar = () => { tentativas = 0; iniciar(true); };
    (async () => {
      try {
        const [{ PushNotifications }, { LocalNotifications }] = await Promise.all([
          import("@capacitor/push-notifications"), import("@capacitor/local-notifications"),
        ]);
        if (cancelado) return;
        push = PushNotifications;
        const add = async (plugin, evento, fn) => {
          const listener = await plugin.addListener(evento, fn);
          if (cancelado) await listener.remove(); else listeners.push(listener);
        };
        await add(push, "registration", token => {
          if (cancelado || !token?.value) return;
          tokenAtual = token.value;
          return salvarToken();
        });
        await add(push, "registrationError", () => { status("erro"); tentarDepois(); });
        await add(push, "pushNotificationActionPerformed", event => abrir(event.notification?.data));
        await add(LocalNotifications, "localNotificationActionPerformed", event => {
          if (event.actionId !== "dismiss") abrir(event.notification?.extra);
        });
        // O plugin exibe o push no Android via presentationOptions, inclusive
        // em primeiro plano. Não agendar uma cópia local nem mostrar toast.
        await iniciar(true);
      } catch (e) {
        status("erro");
        console.warn("Não foi possível preparar as notificações:", e?.code || "push-setup");
      }
    })();
    window.addEventListener("focus", aoRetomar);
    window.addEventListener("online", aoRetomar);
    window.addEventListener("pc-push-retry", aoTentar);
    document.addEventListener("visibilitychange", aoRetomar);
    return () => {
      cancelado = true;
      clearTimeout(retryTimer);
      listeners.forEach(listener => listener.remove());
      window.removeEventListener("focus", aoRetomar);
      window.removeEventListener("online", aoRetomar);
      window.removeEventListener("pc-push-retry", aoTentar);
      document.removeEventListener("visibilitychange", aoRetomar);
    };
  }, [uid, app]);
}
